/**
 * Plotwise App Version & Standalone Deployment Update Service
 *
 * Håndterer:
 * 1. Detektering af nye server-deployments for brugere der kører Plotwise som installeret app (PWA standalone).
 * 2. 1-tap opdatering ("Opdater app" / "Opdater nu"), der rydder Service Workers, CacheStorage
 *    og tvinger browseren/WebKit til at hente den seneste HTML og de nye JS-bundles.
 * 3. Automatisk kontrol ved app-start, ved bruger-login og ved app-resume (visibilitychange).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { isRunningStandalone } from './pwa-install';

// Compile-time defines indsprøjtet af Vite under build (se vite.config.ts)

declare const __APP_BUILD_TIME__: string | undefined;
declare const __APP_COMMIT_SHA__: string | undefined;

export const CLIENT_BUILD_TIME: string =
  typeof __APP_BUILD_TIME__ !== 'undefined' ? __APP_BUILD_TIME__ : '2026-10-23T16:00:00.000Z';

export const CLIENT_COMMIT_SHA: string =
  typeof __APP_COMMIT_SHA__ !== 'undefined' ? __APP_COMMIT_SHA__ : 'dev';

export const APP_VERSION = '2.0.0';

export interface VersionMetadata {
  version: string;
  buildTime: string;
  commit: string;
}

export interface UpdateCheckResult {
  hasUpdate: boolean;
  currentBuildTime: string;
  currentCommit: string;
  serverVersion?: string;
  serverBuildTime?: string;
  serverCommit?: string;
  error?: string;
}

/**
 * Sammenligner klientens indbyggede build/commit med serverens svar.
 */
export function isNewerVersion(
  server: Partial<VersionMetadata>,
  clientTime: string = CLIENT_BUILD_TIME,
  clientCommit: string = CLIENT_COMMIT_SHA,
): boolean {
  if (!server) return false;

  const sCommit = (server.commit || '').trim();
  const cCommit = (clientCommit || '').trim();

  // 1. Hvis begge parter har et specifikt git commit hash (ikke 'dev' eller 'production')
  const isGenericCommit = (val: string) => !val || val === 'dev' || val === 'production';
  if (!isGenericCommit(sCommit) && !isGenericCommit(cCommit)) {
    const isSameCommit =
      sCommit === cCommit ||
      (sCommit.length >= 7 &&
        cCommit.length >= 7 &&
        (sCommit.startsWith(cCommit) || cCommit.startsWith(sCommit)));

    if (!isSameCommit) {
      return true;
    }
  }

  // 2. Hvis serverens buildTime er tidsmæssigt nyere end klientens buildTime
  if (server.buildTime && clientTime) {
    const serverMs = Date.parse(server.buildTime);
    const clientMs = Date.parse(clientTime);
    if (!Number.isNaN(serverMs) && !Number.isNaN(clientMs)) {
      // Mindst 2 sekunders forskel for at undgå millisekund-jitter i builds
      return serverMs - clientMs > 2000;
    }
  }

  return false;
}

/**
 * Tjekker om en nyere deployment er tilgængelig på serveren ved at hente /version.json
 * med 'no-store' for at omgå alle browser- og mellemliggende proxy-caches.
 */
export async function checkForAppUpdate(): Promise<UpdateCheckResult> {
  const baseResult: UpdateCheckResult = {
    hasUpdate: false,
    currentBuildTime: CLIENT_BUILD_TIME,
    currentCommit: CLIENT_COMMIT_SHA,
  };

  if (typeof window === 'undefined') return baseResult;

  try {
    const bust = Date.now();
    // Forsøg først statisk version.json (serveres no-cache af Caddy)
    let res = await fetch(`/version.json?_t=${bust}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    // Fallback til /api/version hvis statisk version.json ikke findes
    if (!res.ok) {
      res = await fetch(`/api/version?_t=${bust}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
    }

    if (!res.ok) {
      return {
        ...baseResult,
        error: `Server returnerede HTTP ${res.status}`,
      };
    }

    const contentType =
      typeof res.headers?.get === 'function' ? res.headers.get('content-type') || '' : '';
    if (contentType && !contentType.includes('application/json')) {
      return {
        ...baseResult,
        error: 'Ugyldigt versionssvar fra server (forventede JSON)',
      };
    }

    const data = (await res.json()) as VersionMetadata;
    const hasUpdate = isNewerVersion(data, CLIENT_BUILD_TIME, CLIENT_COMMIT_SHA);

    return {
      hasUpdate,
      currentBuildTime: CLIENT_BUILD_TIME,
      currentCommit: CLIENT_COMMIT_SHA,
      serverVersion: data.version,
      serverBuildTime: data.buildTime,
      serverCommit: data.commit,
    };
  } catch (err) {
    return {
      ...baseResult,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Udfører 1-tap app-opdatering for installeret PWA:
 * 1. Afregistrerer samtlige service workers.
 * 2. Tømmer al CacheStorage.
 * 3. Nulstiller chunk-reload session guards.
 * 4. Tvinger hard-reload via location.replace med cache-buster, så Safari/iOS WebKit henter nyeste HTML.
 */
export async function performAppUpdate(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    // 1. Unregister all service workers
    const sw =
      typeof navigator !== 'undefined' && 'serviceWorker' in navigator
        ? navigator.serviceWorker
        : undefined;

    if (sw && sw.getRegistrations) {
      try {
        const registrations = await sw.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      } catch {
        // Ignorér fejl og fortsæt opdatering
      }
    }

    // 2. Tøm alle cacher i CacheStorage
    const cacheStorage =
      typeof window !== 'undefined' && 'caches' in window
        ? window.caches
        : typeof caches !== 'undefined'
          ? caches
          : undefined;

    if (cacheStorage && cacheStorage.keys) {
      try {
        const keys = await cacheStorage.keys();
        for (const key of keys) {
          await cacheStorage.delete(key);
        }
      } catch {
        // Ignorér fejl og fortsæt opdatering
      }
    }

    // 3. Gem opdateringstidspunkt og ryd session chunk reload cooldown for at undgå fejl-loop
    try {
      const nowStr = Date.now().toString();
      if (window.sessionStorage) {
        window.sessionStorage.removeItem('plotwise_chunk_reload_ts');
        window.sessionStorage.setItem('plotwise_last_update_ts', nowStr);
      }
      if (window.localStorage) {
        window.localStorage.setItem('plotwise_last_update_ts', nowStr);
      }
    } catch {
      // ignore
    }
  } finally {
    // 4. Force hard reload med unik cache-buster URL
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_pw_update', Date.now().toString());
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }
}

/**
 * Formaterer et opdateringstidspunkt på dansk (f.eks. "I dag kl. 18:45" eller "23. okt. kl. 18:45").
 */
export function formatAppUpdateTimestamp(
  dateInput: string | number | Date | null | undefined,
  nowInput?: Date,
): string {
  if (!dateInput) return '';
  const d =
    typeof dateInput === 'string' || typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput;
  if (Number.isNaN(d.getTime())) return '';

  const now = nowInput || new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const timeStr = `kl. ${hours}:${minutes}`;

  // Tjek om det er i dag (samme kalenderdag i lokal tid)
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (isToday) {
    return `I dag ${timeStr}`;
  }

  // Tjek om det var i går
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();

  if (isYesterday) {
    return `I går ${timeStr}`;
  }

  const monthsDa = [
    'jan.',
    'feb.',
    'mar.',
    'apr.',
    'maj',
    'jun.',
    'jul.',
    'aug.',
    'sep.',
    'okt.',
    'nov.',
    'dec.',
  ];
  const day = d.getDate();
  const month = monthsDa[d.getMonth()];

  if (d.getFullYear() === now.getFullYear()) {
    return `${day}. ${month} ${timeStr}`;
  }

  return `${day}. ${month} ${d.getFullYear()} ${timeStr}`;
}

/**
 * Udleder det effektive opdateringstidspunkt for appen.
 * Vælger den nyeste gyldige tidsangivelse mellem sessionStorage, localStorage og buildTime.
 */
export function getEffectiveAppUpdateTime(buildTime: string = CLIENT_BUILD_TIME): string | number {
  if (typeof window !== 'undefined') {
    try {
      const sTs = window.sessionStorage?.getItem('plotwise_last_update_ts');
      const lTs = window.localStorage?.getItem('plotwise_last_update_ts');
      const tsNum = Math.max(Number(sTs) || 0, Number(lTs) || 0);
      const buildMs = Date.parse(buildTime);
      if (tsNum > 0 && (Number.isNaN(buildMs) || tsNum > buildMs)) {
        return tsNum;
      }
    } catch {
      // ignore
    }
  }
  return buildTime;
}

/**
 * Returnerer en brugervenlig dansk tekststreng for seneste app-opdatering,
 * f.eks. "Senest opdateret: I dag kl. 18:45".
 */
export function getAppLastUpdatedLabel(
  buildTime: string = CLIENT_BUILD_TIME,
  nowInput?: Date,
): string {
  const effectiveTime = getEffectiveAppUpdateTime(buildTime);
  const formatted = formatAppUpdateTimestamp(effectiveTime, nowInput);
  return formatted ? `Senest opdateret: ${formatted}` : '';
}

export interface UseAppUpdateOptions {
  user?: { id?: string; email?: string } | null;
  onUpdateDetected?: (info: UpdateCheckResult) => void;
}

export function useAppUpdate(options?: UseAppUpdateOptions) {
  const [isStandalone, setIsStandalone] = useState<boolean>(() =>
    typeof window !== 'undefined' ? isRunningStandalone() : false,
  );
  const [hasUpdate, setHasUpdate] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateCheckResult | null>(null);

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const lastCheckedAtRef = useRef<number>(0);
  const checkedForUserRef = useRef<string | null>(null);

  // Synkronisér standalone-status
  useEffect(() => {
    setIsStandalone(isRunningStandalone());
  }, []);

  // Ryd op i _pw_update URL-parameter efter vellykket genindlæsning
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.search.includes('_pw_update')) {
      try {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('_pw_update');
        const newRelative = cleanUrl.pathname + cleanUrl.search + cleanUrl.hash;
        window.history.replaceState(null, '', newRelative);
      } catch {
        // ignore
      }
    }
  }, []);

  const checkNow = useCallback(
    async (forcePrompt = false): Promise<UpdateCheckResult> => {
      const now = Date.now();
      // Afvis overflødige parallelle/hurtige netværksforespørgsler medmindre brugeren eksplicit trykker "Tjek"
      if (!forcePrompt && now - lastCheckedAtRef.current < 6000) {
        return {
          hasUpdate: false,
          currentBuildTime: CLIENT_BUILD_TIME,
          currentCommit: CLIENT_COMMIT_SHA,
        };
      }
      lastCheckedAtRef.current = now;
      setIsChecking(true);
      try {
        const res = await checkForAppUpdate();
        setUpdateInfo(res);
        if (res.hasUpdate) {
          setHasUpdate(true);
          // Modalen åbnes automatisk i standalone PWA for ikke at forstyrre almindelig browserbrowsing
          if (isStandalone) {
            setShowUpdateModal(true);
          }
          optionsRef.current?.onUpdateDetected?.(res);
        } else if (forcePrompt) {
          // Hvis brugeren manuelt har trykket "Tjek for opdatering", men der ikke er nogen ny version
          setHasUpdate(false);
        }
        return res;
      } finally {
        setIsChecking(false);
      }
    },
    [isStandalone],
  );

  // 1. Automatisk tjek ved app-start (efter 900ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      void checkNow();
    }, 900);
    return () => clearTimeout(timer);
  }, [checkNow]);

  // 2. Automatisk tjek når brugeren logger ind (auth-state ændres fra null til user)
  const currentUserId = options?.user?.id ?? null;
  useEffect(() => {
    if (currentUserId && currentUserId !== checkedForUserRef.current) {
      checkedForUserRef.current = currentUserId;
      void checkNow();
    }
  }, [currentUserId, checkNow]);

  // 3. Automatisk tjek når appen kommer i forgrunden (mobil multitasking / resume)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkNow();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkNow]);

  const applyUpdate = useCallback(async () => {
    setIsUpdating(true);
    await performAppUpdate();
  }, []);

  const dismissUpdateModal = useCallback(() => {
    setShowUpdateModal(false);
  }, []);

  const effectiveUpdateTime = getEffectiveAppUpdateTime(CLIENT_BUILD_TIME);
  const lastUpdatedLabel = getAppLastUpdatedLabel(CLIENT_BUILD_TIME);
  const formattedBuildTime = formatAppUpdateTimestamp(CLIENT_BUILD_TIME);

  return {
    isStandalone,
    hasUpdate,
    showUpdateModal,
    setShowUpdateModal,
    isChecking,
    isUpdating,
    updateInfo,
    effectiveUpdateTime,
    lastUpdatedLabel,
    formattedBuildTime,
    checkNow,
    applyUpdate,
    dismissUpdateModal,
  };
}
